import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Link2, Undo2 } from "lucide-react"

import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { ReasonDialog } from "@/components/ReasonDialog"
import { Toolbar, ToolbarField } from "@/components/Toolbar"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useAuth, useRbac } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import {
  conciliarMovimiento,
  desconciliarMovimiento,
  fetchConciliacionBancaria,
  type MovimientoConciliable,
  type SugerenciaDeConciliacion,
} from "@/routes/finance/api"
import { useFinanceFilters } from "@/routes/finance/context"

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"]

/**
 * LA CONCILIACIÓN BANCARIA, MOVIMIENTO A MOVIMIENTO.
 *
 * Cesar, 2026-10-04: «ejecuta la conciliación bancaria movimiento a
 * movimiento» y «cuando carguen movimientos de bancos deberías de pre-matchear
 * en base a proveedores, ventas, etc. para no tener que cuadrar a mano».
 *
 * Al cargar el extracto, Marty concilia solo lo inequívoco (ver
 * `conciliacion_bancaria.py`). Aquí se ve el resultado: lo conciliado (y si
 * fue automático), lo que tiene sugerencia para que una persona confirme, y lo
 * que no tiene pareja, que se concilia a mano con motivo. Todo se deshace.
 */
export function ConciliacionBancariaPage() {
  const { activeTenantId } = useAuth()
  const { can } = useRbac()
  const { anio, pais, paisQuery } = useFinanceFilters()
  const { toast } = useToast()
  const qc = useQueryClient()
  const [mes, setMes] = React.useState(() => Math.max(1, new Date().getMonth()))
  const [eligiendo, setEligiendo] = React.useState<{ mov: MovimientoConciliable; sug: SugerenciaDeConciliacion | null } | null>(null)

  const q = useQuery({
    queryKey: ["finance-conciliacion-bancaria", activeTenantId, anio, mes, pais],
    queryFn: () => fetchConciliacionBancaria(anio, mes, paisQuery),
    enabled: Boolean(activeTenantId),
  })
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["finance-conciliacion-bancaria"] })
    void qc.invalidateQueries({ queryKey: ["finance-caja-bancos"] })
  }
  const conciliar = useMutation({
    mutationFn: (d: { id: string; tipo: "factura" | "comprobante" | "manual"; refId: string | null; motivo: string }) =>
      conciliarMovimiento(d.id, d.tipo, d.refId, d.motivo),
    onSuccess: () => { toast({ title: "Movimiento conciliado" }); setEligiendo(null); refrescar() },
    onError: onWriteError("la conciliación"),
  })
  const deshacer = useMutation({
    mutationFn: (id: string) => desconciliarMovimiento(id),
    onSuccess: () => { toast({ title: "Conciliación deshecha" }); refrescar() },
    onError: onWriteError("la conciliación", "deshacer"),
  })

  const d = q.data
  const puedeEditar = can("edit")

  return (
    <div className="space-y-3">
      <Toolbar>
        <ToolbarField label={t("Mes")} htmlFor="conc-mes">
          <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
            <SelectTrigger id="conc-mes" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MESES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{t(m)} {anio}</SelectItem>)}
            </SelectContent>
          </Select>
        </ToolbarField>
      </Toolbar>

      <MetricRow className="lg:grid-cols-4">
        <MetricCard label={t("Movimientos del mes")} value={d?.resumen.movimientos ?? 0} loading={q.isLoading} integer />
        <MetricCard label={t("Conciliados")} value={d?.resumen.conciliados ?? 0} loading={q.isLoading} integer tono="destacado" />
        <MetricCard label={t("Con sugerencia")} value={d?.resumen.sugeridos ?? 0} loading={q.isLoading} integer />
        <MetricCard label={t("Sin pareja")} value={d?.resumen.sin_conciliar ?? 0} loading={q.isLoading} integer
                    tono={(d?.resumen.sin_conciliar ?? 0) > 0 ? "alerta" : "neutro"} />
      </MetricRow>

      <WorkPanel
        title={`${t("Extracto de")} ${t(MESES[mes - 1])} ${anio}`}
        meta={<span className="text-2xs text-muted-foreground">{t("cada movimiento, con qué se explica")}</span>}
        footnote={d?.nota}
      >
        <DataState
          loading={q.isLoading}
          error={q.error}
          empty={Boolean(d) && (d?.movimientos.length ?? 0) === 0}
          emptyMessage={t("No hay movimientos bancarios cargados para este mes.")}
          emptyHint={t("Carga el extracto en Contabilidad › Maestros › Carga por archivo › «Movimientos bancarios». Al cargarlo, Marty concilia solo lo inequívoco.")}
          onRetry={() => void q.refetch()}
          rows={6}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t("Fecha")}</TableHead>
                <TableHead className="w-40">{t("Cuenta")}</TableHead>
                <TableHead>{t("Movimiento")}</TableHead>
                <TableHead className="w-32 text-right">{t("Importe")}</TableHead>
                <TableHead className="w-28">{t("Estado")}</TableHead>
                <TableHead className="min-w-[18rem]">{t("Se explica con")}</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(d?.movimientos ?? []).map((m) => (
                <TableRow key={m.id} className={cn(m.estado === "sin_conciliar" && "bg-warning-soft/30")}>
                  <TableCell className="code text-2xs text-muted-foreground">{formatDate(m.fecha)}</TableCell>
                  <TableCell className="truncate text-2xs" title={m.cuenta}>{m.cuenta}</TableCell>
                  <TableCell className="text-xs">
                    <span className="block truncate" title={m.descripcion ?? ""}>{m.descripcion || "—"}</span>
                    <span className="text-2xs text-muted-foreground">
                      {m.contraparte ? `${m.contraparte} · ` : ""}{m.referencia ? `ref. ${m.referencia}` : ""}
                      {m.categoria ? ` · ${m.categoria}` : ""}
                    </span>
                  </TableCell>
                  <TableCell className={cn("num", m.importe < 0 && "text-destructive")}>
                    {formatAmount(m.importe)} <span className="text-2xs text-muted-foreground">{m.moneda}</span>
                  </TableCell>
                  <TableCell>
                    {m.estado === "conciliado" ? (
                      <Badge variant="success-soft" estado>{m.conciliado?.automatica ? t("conciliado · Marty") : t("conciliado")}</Badge>
                    ) : m.estado === "sugerido" ? (
                      <Badge variant="brand-soft" estado>{t("sugerido")}</Badge>
                    ) : (
                      <Badge variant="warning-soft" estado>{t("sin pareja")}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-2xs">
                    {m.conciliado ? (
                      <span>
                        <span className="font-medium">{m.conciliado.etiqueta}</span>
                        {m.conciliado.motivo ? <span className="block text-muted-foreground">{m.conciliado.motivo}</span> : null}
                      </span>
                    ) : m.sugerencias.length ? (
                      <ul className="space-y-1">
                        {m.sugerencias.map((s) => (
                          <li key={s.id} className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium">{s.etiqueta}</span>
                            <span className="text-muted-foreground">{s.por_que}</span>
                            {puedeEditar ? (
                              <Button size="xs" variant={s.confianza === "exacta" ? "default" : "outline"}
                                      onClick={() => setEligiendo({ mov: m, sug: s })}>
                                <Check />
                                {t("Es ésta")}
                              </Button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-muted-foreground">{t("Ninguna factura ni comprobante coincide.")}</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    {!puedeEditar ? null : m.conciliado ? (
                      <Button size="xs" variant="ghost" onClick={() => deshacer.mutate(m.id)} disabled={deshacer.isPending}>
                        <Undo2 />
                        {t("Deshacer")}
                      </Button>
                    ) : (
                      <Button size="xs" variant="ghost" onClick={() => setEligiendo({ mov: m, sug: null })}>
                        <Link2 />
                        {t("A mano")}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DataState>
      </WorkPanel>

      {eligiendo ? (
        <ReasonDialog
          open
          onOpenChange={(v) => { if (!v) setEligiendo(null) }}
          title={eligiendo.sug ? `${t("Conciliar con")} ${eligiendo.sug.etiqueta}` : t("Conciliar a mano")}
          description={eligiendo.sug
            ? `${formatAmount(eligiendo.mov.importe)} ${eligiendo.mov.moneda} · ${eligiendo.sug.por_que}.`
            : t("Para lo que no tiene factura ni comprobante: una transferencia entre cuentas propias, una comisión, un impuesto. Di qué es.")}
          confirmLabel={t("Conciliar")}
          pending={conciliar.isPending}
          suggestions={eligiendo.sug
            ? ["Confirmado contra el extracto."]
            : ["Transferencia entre cuentas propias.", "Comisión bancaria.", "Pago de impuestos."]}
          onConfirm={(motivo) => conciliar.mutateAsync({
            id: eligiendo.mov.id,
            tipo: eligiendo.sug ? eligiendo.sug.tipo : "manual",
            refId: eligiendo.sug ? eligiendo.sug.id : null,
            motivo,
          }).then(() => undefined)}
        />
      ) : null}
    </div>
  )
}
